import React from 'react';
import type { VoiceStatus } from '@troia-v3/types';
import { StatusPill } from '@/shared/components/ui';
import type { StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Apresentação compartilhada entre VoiceCard, VoiceListItem e a página.
 * Mantém o mapeamento de provider/status em um único lugar (DRY).
 */

type ProviderStyle = { label: string; color: string };

const PROVIDER_STYLES: Array<{ match: RegExp; label: string; color: string }> = [
  { match: /eleven/i, label: 'ElevenLabs', color: '#7c3aed' },
  { match: /openai/i, label: 'OpenAI', color: '#0f9d6b' },
  { match: /google/i, label: 'Google', color: '#4285f4' },
  { match: /azure/i, label: 'Azure', color: '#0078d4' },
];

/** Cor/rótulo do provider — best-effort a partir do `providerId` cru, fallback slate. */
export const getProviderStyle = (providerId?: string): ProviderStyle => {
  const id = providerId ?? '';
  const found = PROVIDER_STYLES.find((p) => p.match.test(id));
  return found ?? { label: id || 'Desconhecido', color: '#64748b' };
};

/** Caixa de ícone (mic) colorida pelo provider — identidade visual da voz (não há avatar/foto). */
export const VoiceIconBox: React.FC<{ providerId?: string; className?: string; iconClassName?: string }> = ({
  providerId,
  className = 'w-10 h-10 rounded-[11px]',
  iconClassName = 'w-5 h-5',
}) => {
  const { color } = getProviderStyle(providerId);
  return (
    <div
      className={`flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ backgroundColor: color, color: '#ffffff' }}
    >
      <Icon name="mic" className={iconClassName} />
    </div>
  );
};

/** Badge de provider — ponto colorido + rótulo. */
export const ProviderBadge: React.FC<{ providerId?: string }> = ({ providerId }) => {
  const { label, color } = getProviderStyle(providerId);
  return (
    <span
      className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md text-xs font-medium"
      style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-none" style={{ backgroundColor: color }} />
      <span className="truncate max-w-[120px]">{label}</span>
    </span>
  );
};

const STATUS_CONFIG: Record<VoiceStatus, { label: string; variant: StatusPillVariant }> = {
  active: { label: 'Ativa', variant: 'success' },
  inactive: { label: 'Inativa', variant: 'neutral' },
  processing: { label: 'Processando', variant: 'warning' },
  failed: { label: 'Falhou', variant: 'error' },
};

/** Pill de status — StatusPill shared (F62); cobre os 4 estados reais. */
export const VoiceStatusPill: React.FC<{ status: VoiceStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.inactive;
  return <StatusPill variant={cfg.variant} label={cfg.label} />;
};
